import { z } from "zod";
import { IdSchema, IsoTimestampSchema, NonEmptyStringSchema, NonNegativeIntegerSchema, PositiveIntegerSchema, UnknownRecordSchema } from "./source.js";
export const RuntimeProviderSchema = z.enum(["gemini", "openai", "mock", "disabled"]);
/**
 * Provider usage that was reported by an engine stage's terminal artifact.
 * This is intentionally separate from the token fields on LlmUsageTrace:
 * zero is a valid reported free stage, while an unmeasured stage must not be
 * represented as a zero-cost call.
 */
export const EngineCostTelemetrySchema = z
    .object({
    source: z.literal("engine-terminal-artifact"),
    stageId: NonEmptyStringSchema,
    status: z.enum(["measured", "unmeasured"]),
    artifactPath: NonEmptyStringSchema.optional(),
    artifactSha256: z.string().regex(/^[a-f0-9]{64}$/i).optional(),
    inputTokens: NonNegativeIntegerSchema.nullable(),
    outputTokens: NonNegativeIntegerSchema.nullable(),
    completedCalls: NonNegativeIntegerSchema.nullable(),
    reportedCostGbp: z.number().nonnegative().optional(),
    reportedCostUsd: z.number().nonnegative().optional(),
    engineUsdToGbp: z.number().positive().optional(),
    platformUsdToGbp: z.number().positive(),
    conversionDiscrepancy: NonEmptyStringSchema.optional(),
    reason: NonEmptyStringSchema.optional()
})
    .strict();
export const BudgetHardStopBehaviorSchema = z.enum([
    "reject_request",
    "stop_after_current_call",
    "degrade_to_mock",
    "disable_provider"
]);
export const BudgetPolicySchema = z
    .object({
    id: IdSchema,
    orgId: IdSchema.optional(),
    projectId: IdSchema.optional(),
    runId: IdSchema.optional(),
    tokenCap: PositiveIntegerSchema.nullable(),
    perProjectCallCap: PositiveIntegerSchema.nullable(),
    perRunCallCap: PositiveIntegerSchema.nullable(),
    unmeteredFailureProjectCap: PositiveIntegerSchema,
    unmeteredFailureWindowMs: PositiveIntegerSchema,
    pinnedModelId: NonEmptyStringSchema,
    provider: RuntimeProviderSchema,
    hardStopBehavior: BudgetHardStopBehaviorSchema,
    warningThresholdPercent: z.number().min(0).max(100).optional(),
    createdAt: IsoTimestampSchema.optional()
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.perProjectCallCap !== null &&
        value.perRunCallCap !== null &&
        value.perRunCallCap > value.perProjectCallCap) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "perRunCallCap must not exceed perProjectCallCap",
            path: ["perRunCallCap"]
        });
    }
});
export const LlmUsageTraceSchema = z
    .object({
    id: IdSchema,
    projectId: IdSchema.optional(),
    runId: IdSchema.optional(),
    turnId: IdSchema.optional(),
    toolCallTraceId: IdSchema.optional(),
    provider: RuntimeProviderSchema,
    model: NonEmptyStringSchema,
    promptTokens: NonNegativeIntegerSchema,
    completionTokens: NonNegativeIntegerSchema,
    totalTokens: NonNegativeIntegerSchema,
    cachedPromptTokens: NonNegativeIntegerSchema.optional(),
    requestId: NonEmptyStringSchema.optional(),
    recordedAt: IsoTimestampSchema.optional(),
    engineCost: EngineCostTelemetrySchema.optional(),
    metadata: UnknownRecordSchema.optional()
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.totalTokens !== value.promptTokens + value.completionTokens) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "totalTokens must equal promptTokens plus completionTokens",
            path: ["totalTokens"]
        });
    }
});
export const ProviderDegradationModeSchema = z.enum([
    "fail_closed",
    "use_fallback_provider",
    "degrade_to_mock",
    "disable_llm"
]);
export const ProviderRetryPolicySchema = z
    .object({
    maxAttempts: PositiveIntegerSchema,
    backoffMs: NonNegativeIntegerSchema,
    retryableStatusCodes: z.array(PositiveIntegerSchema)
})
    .strict();
export const ProviderRuntimePolicySchema = z
    .object({
    id: IdSchema,
    provider: RuntimeProviderSchema,
    allowedProviders: z.array(RuntimeProviderSchema).min(1),
    modelId: NonEmptyStringSchema,
    fallbackProvider: RuntimeProviderSchema.optional(),
    degradationMode: ProviderDegradationModeSchema,
    retry: ProviderRetryPolicySchema,
    timeoutMs: PositiveIntegerSchema,
    budgetPolicyId: IdSchema.optional(),
    disabledReason: NonEmptyStringSchema.optional()
})
    .strict()
    .superRefine((value, ctx) => {
    if (!value.allowedProviders.includes(value.provider)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "provider must be listed in allowedProviders",
            path: ["allowedProviders"]
        });
    }
    if (value.provider === "disabled" && value.disabledReason === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "disabled provider policy requires disabledReason",
            path: ["disabledReason"]
        });
    }
});
//# sourceMappingURL=budget.js.map